import { ref, onBeforeUnmount } from 'vue'
import NewBsBox from '@xhs/yam-beer/cmp/primitive/NewBsBox'
import BeerIcon from '@xhs/yam-beer/cmp/common/Icon/Icon.vue'
import BeerInput from '@xhs/yam-beer/cmp/dataEntry/Input/Input.vue'
import { copyToClipboard } from '@xhs/yam-beer/utils/urlUtils'
import Tooltip from '../../overlayers/Tooltip/Tooltip.vue'

// @ts-ignore
const ActionText = (props, { slots, attrs }) => (
  <NewBsBox
    as="span"
    bs={{ cmp: 'textStyle', fontSize: 'inherit' }}
    context={{ variant: 'link' }}
    {...{
      ...attrs,
    }}
  >
    {slots.default?.()}
  </NewBsBox>
)

type EllipsisOptions = {
  expandable: boolean
  maxLength: number
  suffix: string
  symbols: [string, string]
  tooltip: string
  onExpand?: () => void
  onCollapse?: () => void
}

const defaultEllipsisOptions = {
  expandable: false,
  maxLength: 60,
  suffix: '...',
  symbols: ['展开', '收起'],
  tooltip: '',
}

export function useEllipsis(opts: EllipsisOptions | boolean) {
  if (!opts) {
    return {
      renderExpand(content: any) { return null },
      getContent(c: unknown) { return c }
    }
  }
  const options = { ...defaultEllipsisOptions, ...(typeof opts !== 'boolean' ? opts : {}) }
  const isExpand = ref(false)
  const [expandText, collapseText] = options.symbols
  function collpase() {
    isExpand.value = false
  }
  function expand() {
    isExpand.value = true
  }
  return {
    renderExpand(content: any) {
      const children = content?.[0]?.children
      if (typeof children !== 'string') {
        return null
      }
      if (isExpand.value) {
        return (
          <ActionText onClick={collpase}>{collapseText}</ActionText>
        )
      }
      return [
        options.tooltip ? (
          // @ts-ignore
          <Tooltip fixed={false} content={options.tooltip}>
            <span>{options.suffix}</span>
          </Tooltip>
        ) : (<span title={children}>{options.suffix}</span>),
        options.expandable ? <ActionText onClick={expand}>{expandText}</ActionText> : null,
      ]
    },
    getContent(content: any) {
      const children = content?.[0]?.children
      if (typeof children !== 'string') {
        return null
      }
      if (isExpand.value) {
        return children
      }
      return children.slice(0, options.maxLength)
    }
  }
}

type CopyOption = {
  name: 'copy'
  onCopy?: (content: string) => void
  icons: [string, string]
  tooltips: [string, string]
}
type EditOption = {
  name: 'edit'
  icon: string
  tooltip: string
  onChange?: (text: string) => void
  onStart?: () => void
  onEnd?: () => void
  onCancel?: () => void
}

const defaultCopyOption = {
  name: 'copy',
  icons: ['copy', 'done'],
  tooltips: ['复制', '复制成功']
}

const defaultEditOption = {
  name: 'edit',
  icon: 'edit',
  tooltip: '编辑',
}

export type ActionOption = CopyOption | EditOption | 'copy' | 'edit' | undefined

export function useAction(opts: ActionOption) {
  const isEdit = ref(false)
  let options: CopyOption | EditOption
  if (opts === 'copy') {
    // @ts-ignore
    options = { ...defaultCopyOption }
  } else if (opts === 'edit') {
    // @ts-ignore
    options = { ...defaultEditOption }
  } else if (opts?.name === 'copy') {
    options = { ...defaultCopyOption, ...opts }
  } else if (opts?.name === 'edit') {
    options = { ...defaultEditOption, ...opts }
  } else {
    return {
      isEdit,
      renderEdit(content: unknown) { return null },
      renderAction(content: unknown) { return null }
    }
  }

  const copied = ref(false)
  let timer: NodeJS.Timeout | undefined

  onBeforeUnmount(() => {
    if (timer) {
      clearTimeout(timer)
      timer = undefined
    }
  })
  function handleCopy(content: string) {
    if (!copied.value) {
      copyToClipboard(content)
      copied.value = true
      if ('onCopy' in options) {
        options.onCopy?.(content)
      }
      timer = setTimeout(() => {
        copied.value = false
        timer = undefined
      }, 3000)
    }
  }

  function handleEdit() {
    if ('onStart' in options) {
      options.onStart?.()
    }
    isEdit.value = true
  }

  function handleUpdate(newContent: string) {
    if ('onChange' in options) {
      options.onChange?.(newContent)
    }
  }

  function handleKey(e: KeyboardEvent) {
    if (e.keyCode === 13) {
      isEdit.value = false
      if ('onEnd' in options) {
        options.onEnd?.()
      }
    }
    if (e.keyCode === 27) {
      isEdit.value = false
      if ('onCancel' in options) {
        options.onCancel?.()
      }
    }
  }

  function handleBlur() {
    isEdit.value = false
    if ('onEnd' in options) {
      options.onEnd?.()
    }
  }

  return {
    isEdit,
    renderEdit(content: any) {
      const children = content?.[0]?.children
      if (typeof children !== 'string') {
        return null
      }
      return (
        <BeerInput bs={{ width: '100%' }} modelValue={children} {...{
          autoFocus: true,
          'onUpdate:modelValue': handleUpdate,
          'onBlur': handleBlur,
          'onKeyup': handleKey,
        }} />
      )
    },
    renderAction(content: any) {
      const children = content?.[0]?.children
      if (typeof children !== 'string') {
        return null
      }
      if (options.name === 'copy') {
        const [beforeIcon, afterIcon] = options.icons
        const [beforeTip, afterTip] = options.tooltips
        const tips = copied.value ? afterTip : beforeTip
        return (
          // @ts-ignore
          <Tooltip fixed={false} content={tips} hidden={!tips}>
            <BeerIcon
              color={copied.value ? 'success' : 'primary'}
              bs={{ verticalAlign: 'text-bottom' }}
              {...{ onClick: () => handleCopy(children) }}
              icon={copied.value ? afterIcon : beforeIcon}
            />
          </Tooltip>
        )
      }
      if (options.name === 'edit' && !isEdit.value) {
        return (
          // @ts-ignore
          <Tooltip fixed={false} hidden={!options.tooltip} content={options.tooltip}>
            <BeerIcon
              color="primary"
              bs={{ verticalAlign: 'text-bottom' }}
              {...{ onClick: handleEdit }}
              icon={options.icon}
            />
          </Tooltip>
        )
      }
      return null
    }
  }
}
